import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useTerminalPage } from '@/hooks/Dashboard/useTerminalPage';
import { DropdownField, LoadingSpinner } from '@/components';
import { MultipleMetricCard, SingleMetricCard } from '@/components/Cards';
import OneLabelTwoBarsChart from '@/components/Charts/OneLabelTwoBarsChart/OneLabelTwoBarsChart';
import { getCurrentDateTime } from '@/utils/getCurrentDateTime';
import {
  OcupacionArmador,
  OcupacionTerminal,
} from '@/types/dashboard/TeminalPage';

import ShipChartCard from '../ShipPage/components/ShipChartCard/ShipChartCard';
import shipIconChartCard from '../../../../../assets/icons/dashboard/machine.png';
import OccupationCard from './components/OccupationCard/OccupationCard';
import occupationIcon1 from '../../../../../assets/icons/dashboard/mcn_icon_llenos.svg';
import occupationIcon2 from '../../../../../assets/icons/dashboard/mcn_icon_vacios.svg';
import occupationIcon3 from '../../../../../assets/icons/dashboard/OcupationCard3.svg';
import styles from './TerminalPage.module.scss';

type Metric = {
  icon: string;
  title: string;
  legend: string;
  metric: string;
  completion: number;
  color: string;
};

const icons = [occupationIcon1, occupationIcon2, occupationIcon3];
const colors = ['#4CAF50', '#FF9800', '#FF9800'];

const getMetricsForOccupationCard = (
  outputArray: OcupacionTerminal[],
  metricIcons: string[],
  metricTitles: string[],
  metricColors: string[]
): Metric[] => {
  return outputArray.map((item, index) => ({
    icon: metricIcons[index],
    title: metricTitles[index],
    legend: index === 2 ? 'Ctrs' : 'TEUs',
    metric: item.numero.toString(),
    completion: parseFloat(item.porcentaje.toFixed(1)),
    color: metricColors[index],
  }));
};

const returnArmadorColor = (armador: string): string => {
  const armadorLower = armador.toLowerCase();

  const armadorColors: { [key: string]: string } = {
    evergreen: '#3C7D22',
    cosco: '#215C98',
    cma: '#215C98',
    hyundai: '#215C98',
    pil: '#C00000',
    'yang ming': '#C00000',
    msc: '#FFFF00',
    hlcl: '#FF9900',
  };

  return armadorColors[armadorLower] || 'purple';
};

export const TerminalPage = () => {
  const { t, i18n } = useTranslation('dashboardPage');
  const {
    armadoresListForDropdown,
    currentArmador,
    loading,
    setCurrentArmador,
    terminalPageData,

    // pageData,
  } = useTerminalPage();
  const getChartCardLegend = (fecha: string): string => {
    const date = new Date(fecha);

    // Calcular la fecha a una semana después
    const fechaAUnaSemana = new Date(date);
    fechaAUnaSemana.setDate(date.getDate() + 6);

    // Obtener el nombre del mes en inglés
    const monthNamesEn = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];
    const monthNamesEs = [
      'Enero',
      'Febrero',
      'Marzo',
      'Abril',
      'Mayo',
      'Junio',
      'Julio',
      'Agosto',
      'Septiembre',
      'Octubre',
      'Noviembre',
      'Diciembre',
    ];

    const day = date.getDate();
    const monthNames = i18n.language.includes('es')
      ? monthNamesEs
      : monthNamesEn;
    const monthName = monthNames[date.getMonth()]?.toLowerCase();
    const year = date.getFullYear();

    const dayPosterior = fechaAUnaSemana.getDate();
    const monthNamePosterior =
      monthNames[fechaAUnaSemana.getMonth()]?.toLowerCase();

    // Formatear el string final
    return `${t('terminalPage.semana')} ${t('terminalPage.fromDate')} ${day} ${monthName} ${year} ${t('terminalPage.toDate')} ${dayPosterior} ${monthNamePosterior} ${year}`;
  };
  const lastUpdate = useMemo(() => {
    const lastNight = new Date();
    lastNight.setDate(lastNight.getDate() - 1);
    lastNight.setHours(23, 59, 59, 999);
    return getCurrentDateTime(lastNight);
  }, []);
  let ocupacionTerminalArray: OcupacionTerminal[] = [];
  let ocupacionArmadorArray: OcupacionArmador[] = [];
  const ocupacionTerminalData =
    terminalPageData?.ocupacionIndicadores.ocupacionTerminal;
  if (Array.isArray(ocupacionTerminalData)) {
    ocupacionTerminalArray = ocupacionTerminalData;
  } else if (ocupacionTerminalData) {
    ocupacionTerminalArray = [ocupacionTerminalData];
  }
  const ocupacionArmadorData =
    terminalPageData?.ocupacionIndicadores.ocupacionArmador;
  if (Array.isArray(ocupacionArmadorData)) {
    ocupacionArmadorArray = ocupacionArmadorData;
  } else if (ocupacionArmadorData) {
    ocupacionArmadorArray = [ocupacionArmadorData];
  }

  const titles = [
    t('terminalPage.llenos'),
    t('terminalPage.vacios'),
    t('terminalPage.refeer'),
  ];

  const metricsTerminal = getMetricsForOccupationCard(
    ocupacionTerminalArray,
    icons,
    titles,
    colors
  );
  const metricsArmador = getMetricsForOccupationCard(
    ocupacionArmadorArray,
    icons,
    titles,
    colors
  );
  const legend1ForChartCard = {
    color: '#4780bf',
    text: 'Terminal',
  };

  const legend2ForChartCard = {
    color: returnArmadorColor(currentArmador.value),
    text: currentArmador.value,
  };
  const handleArmadorChange = (e: {
    value: string | null;
    target?: { name: string; value?: unknown };
  }) => {
    setCurrentArmador({
      value: (e.target?.value as string) ?? '',
      name: e.target?.name ?? currentArmador.name,
    });
  };

  if (loading)
    return (
      <div style={{ overflow: 'hidden' }} className={styles.dashboard}>
        <LoadingSpinner className={styles.spinner} />
      </div>
    );

  const horasTotales = terminalPageData?.berthUtilizacion?.utilization
    ?.slice(0, 2)
    .reduce((acc, curr) => acc + curr.horasMuelle, 0);

  return (
    <>
      <div className={styles.pageContainer}>
        <div className={styles.pageHeader}>
          <div className={styles.filterAndUpdatedAtContainer}>
            <DropdownField
              id="armador"
              name="armador"
              options={armadoresListForDropdown}
              value={currentArmador.value}
              className={styles.selectDropdown}
              label={t('shipPage.armador')}
              placeholder={t('shipPage.seleccionar_armador')}
              onChange={handleArmadorChange}
            />
          </div>
        </div>
      </div>
      <div className={styles.headerDates}>
        <p className={styles.lastUpdate}>
          {t('shipPage.ultima_actualizacion')}
          <span>{lastUpdate}</span>
        </p>

        <p className={styles.dateLegend}>
          {getChartCardLegend(
            terminalPageData?.ocupacionGrafica.itemsGrafica[0].fecha ?? ''
          )}
        </p>
      </div>
      <div className={styles.dashboard}>
        <div className={styles.leftColumn}>
          <OccupationCard
            title={t('terminalPage.ocupacion_tcm')}
            metrics={metricsTerminal}
          />
          <OccupationCard
            backgroundColor="#ddedff"
            chartstrokecolor="#FFFF"
            title={`${t('terminalPage.ocupacion_terminal')} (${currentArmador.value})`}
            metrics={metricsArmador}
          />
        </div>

        <div className={styles.rightColumn}>
          <div className={styles.historicalProductivityCard}>
            <ShipChartCard
              title={t('terminalPage.ocupacion_tcm')}
              legend=""
              shipImage={shipIconChartCard}
              imgWidth="217px"
              imgHeight="229px"
              legend1={legend2ForChartCard}
              legend2={legend1ForChartCard}
              chart={
                <OneLabelTwoBarsChart
                  data={terminalPageData?.ocupacionGrafica.itemsGrafica ?? []}
                  colorForMetric={returnArmadorColor(currentArmador.value)}
                />
              }
            />
          </div>
          <div className={styles.summaryContainer}>
            <div className={styles.upperSummary}>
              <SingleMetricCard
                title={t('terminalPage.export_dry_dwell_time')}
                detail={
                  terminalPageData?.dwellTime.exportDry.toFixed(1).toString() ??
                  '0'
                }
                backgroundColor="#4780bf"
                textColor="#FFFFFF"
              />
              <SingleMetricCard
                title={t('terminalPage.export_reefer_dwell_time')}
                detail={
                  terminalPageData?.dwellTime.exportReefer
                    .toFixed(1)
                    .toString() ?? '0'
                }
                backgroundColor="#8b3b83"
                textColor="#FFFFFF"
              />
              <SingleMetricCard
                title={t('terminalPage.ts_dwell_time')}
                detail={
                  terminalPageData?.dwellTime.ts.toFixed(1).toString() ?? '0'
                }
                backgroundColor="#629e77"
                textColor="#FFFFFF"
              />
              <SingleMetricCard
                title={t('terminalPage.reposicion_importacion_dwell_time')}
                detail={
                  terminalPageData?.dwellTime.repositionImport
                    .toFixed(1)
                    .toString() ?? '0'
                }
                backgroundColor="#223466"
                textColor="#FFFFFF"
              />
            </div>
            <MultipleMetricCard
              backgroundColor="#223466"
              titleColor="#7dd19d"
              metricsColor="#7dd19d"
              labelsColor="#FFFFFF"
              title={t('terminalPage.totales')}
              label1={t('terminalPage.horas_muelle')}
              metric1={horasTotales?.toString() ?? '0'}
              label2={t('terminalPage.horas_ocupacion')}
              metric2={
                terminalPageData?.berthUtilizacion?.horasOcupacionTotal.toString() ??
                '0'
              }
              label3={t('terminalPage.porcentaje_ocupacion')}
              metric3={`${Math.round(terminalPageData?.berthUtilizacion?.porcentajeOcupacionTotal ?? 0)}`}
            />
          </div>
        </div>
      </div>
    </>
  );
};
